import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  EmperorCardTypes,
  EmperorUIButtonTypes,
} from '@boldpenguin/emperor-presentational';
import { BpSdkStore } from '@boldpenguin/sdk';
import { environment } from '@environments/environment';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { BpSdkCoreService } from 'src/app/core/services/bp-sdk-core-service/bp-sdk-core.service';
import { BpSdkStoreService } from 'src/app/core/services/bp-sdk-store-service/bp-sdk-store.service';
import {
  MixpanelEventType,
  MixpanelService,
} from 'src/app/core/services/mixpanel-service/mixpanel.service';
import { Messages } from 'src/app/core/services/notification-service/notification-messages';
import { NotificationService } from 'src/app/core/services/notification-service/notification.service';
import { selectRedirectPath } from 'src/app/core/store/redirect/redirect.selectors';
import { getRedirectUri } from '../../utilities/redirect-utilities';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit {
  readonly forgotPasswordUrl = `${environment.apiAuthUri}/v2/password/new`;
  readonly signupRoute = '/signup';
  readonly EmperorCardTypes = EmperorCardTypes;
  readonly EmperorUIButtonTypes = EmperorUIButtonTypes;
  readonly environment = environment;

  loginForm: UntypedFormGroup;
  bpSdkStoreState: BpSdkStore;
  userInfo$: Observable<any>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private mixpanelService: MixpanelService,
    private bpSdkCoreService: BpSdkCoreService,
    private bpSdkStoreService: BpSdkStoreService,
    private notificationService: NotificationService,
    private store: Store,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.setSubscriptions();
  }

  gotoUnifiedAuth(): void {
    if (!!this.bpSdkStoreState) {
      this.store
        .pipe(select(selectRedirectPath))
        .pipe(
          distinctUntilChanged(),
          take(1),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((redirectPath) => {
          const redirectUri = getRedirectUri(redirectPath);
          this.bpSdkCoreService.loginUnified(redirectUri);
        });
    }
  }

  onTagClick(route: string): void {
    this.mixpanelService.track(MixpanelEventType.AUTH_DASH_BUTTON_CLICKED, {
      routeChange: route,
    });
  }

  private async navigateAfterLogin(redirectPath: string): Promise<boolean> {
    return (
      this.router
        .navigateByUrl(redirectPath)
        // gracefully send user to quote dashboard if redirect fails
        .catch(() => this.router.navigateByUrl('/quotes/dashboard'))
    );
  }

  private setSubscriptions(): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => this.checkQueryParamsForError(params));

    // Check the SDK to see if there's a token in there, if so, we know we're logged in
    this.bpSdkStoreService.state$
      .pipe(
        map((state) => state.auth),
        withLatestFrom(this.store.pipe(select(selectRedirectPath))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([config, redirectPath]) => {
        if (config && config.token) {
          this.navigateAfterLogin(redirectPath);
        }
        return;
      });

    this.bpSdkStoreService.state$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        this.bpSdkStoreState = state;
      });
  }

  private checkQueryParamsForError(params: Params) {
    if (params.errorMessage) {
      const msg =
        params.errorMessage === 'no-invite'
          ? Messages.NO_INVITE
          : Messages.DEFAULT;
      this.notificationService.error(msg);
      this.mixpanelService.track(MixpanelEventType.LOGIN_AUTH_ERROR, {
        error: params.errorMessage,
      });
    }
  }
}
